import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const FALLBACK_CENTER = [30.6146, -96.3417];

const BASE_COLORS = {
  library: "#1d4ed8",
  dining: "#F59E0B",
  rec: "#16a34a",
  landmark: "#52525b",
  bus: "#0a0a0a",
  user: "#dc2626",
};

function pin(color, label, size = 30) {
  return L.divIcon({
    className: "",
    html: `<div class="pin" style="width:${size}px;height:${size}px;background:${color};color:#fff;font-size:${size > 26 ? 11 : 9}px;">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Bus-stop pin colored by the route(s) that serve it (solid, or a conic gradient for transfer stops).
function stopPin(background, multi, size = 22) {
  return L.divIcon({
    className: "",
    html: `<div class="pin" style="width:${size}px;height:${size}px;background:${background};color:#fff;font-size:9px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);">`
      + `<span style="text-shadow:0 1px 2px rgba(0,0,0,.6)">B</span></div>`
      + (multi ? `<div style="position:absolute;inset:0;border-radius:999px;box-shadow:inset 0 0 0 3px rgba(255,255,255,.25)"></div>` : ""),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Live vehicle marker (Uber-style), colored by its route; the .live-bus CSS glides it between updates.
function busIcon(color, heading) {
  const rot = heading != null ? `transform:rotate(${heading}deg);` : "";
  return L.divIcon({
    className: "live-bus",
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 7px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;">`
      + `<div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:10px solid #fff;${rot}"></div></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function Recenter({ point }) {
  const map = useMap();
  useEffect(() => {
    if (point) map.flyTo(point, 16, { duration: 0.6 });
  }, [point, map]);
  return null;
}

function MapReady({ onReady }) {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  return null;
}

export default function CampusMap({ campus, userLoc, legs = [], filters, focus, mode = "full", myBuildingIds = [], myAmenityIds = [], showPhones = false, showAccess = false, showAthletics = false, showHealth = false, showServices = false, showDorms = false, showGreek = false, onSetOrigin = null, highlightIdx = null, highlightBuildingId = null, collegeFilter = "", walker = null, dark = false }) {
  const [buses, setBuses] = useState([]);
  const [mapObj, setMapObj] = useState(null);
  const busLive = !!campus && mode !== "mine" && !!filters?.bus;
  useEffect(() => {
    if (!busLive) { setBuses([]); return; }
    let alive = true;
    const poll = () => api.get("/transit/realtime")
      .then((r) => { if (alive) setBuses(r.data?.live ? (r.data.vehicles || []) : []); })
      .catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [busLive]);
  if (!campus) return null;
  const brand = campus.school?.theme?.primary || "#500000";
  const CENTER = campus.school?.center || FALLBACK_CENTER;
  const COLORS = { ...BASE_COLORS, academic: brand, student_center: brand };
  const bcode = (b) => (b.code || b.name).slice(0, 2).toUpperCase();
  const mine = mode === "mine";
  const hlActive = highlightIdx != null;
  const bOpacity = (b) => {
    if (hlActive) return b.id === highlightBuildingId ? 1 : 0.25;
    // College filter dims only unrelated buildings — a student's own class buildings
    // (and libraries/hubs, which carry no college) always stay fully visible.
    if (collegeFilter && b.college && b.college !== collegeFilter && !myBuildingIds.includes(b.id)) return 0.15;
    return 1;
  };
  const phones = campus.emergency_phones || [];
  const access = campus.accessibility || [];
  const athletics = campus.athletics || [];
  const health = campus.health || [];
  const services = campus.services || [];
  const dorms = campus.dorms || [];
  const greek = campus.greek || [];
  const accIcon = (t) => (t === "elevator" ? "⇅" : t === "ramp" ? "◺" : t === "closure" ? "⚠" : "♿");
  const accColor = (t) => (t === "closure" ? "#dc2626" : "#0369a1");

  const buildingList = mine
    ? campus.buildings.filter((b) => myBuildingIds.includes(b.id))
    : campus.buildings;
  const amenityList = mine
    ? campus.amenities.filter((a) => myAmenityIds.includes(a.id))
    : campus.amenities;
  const showBuildings = mine || filters.buildings;
  const showAmenities = mine || filters.amenities;
  const showBus = !mine && filters.bus;
  const transitSrc = campus.school?.transit_source;
  const legendItems = [
    showBuildings && { label: "Academic", color: COLORS.academic },
    showAmenities && { label: "Dining", color: COLORS.dining },
    showAmenities && { label: "Library", color: COLORS.library },
    showAmenities && { label: "Rec/Gym", color: COLORS.rec },
    showBus && { label: "Bus", color: COLORS.bus },
    showPhones && { label: "Safety", color: "#1d4ed8" },
    showAccess && { label: "Accessible", color: "#0369a1" },
    showAthletics && { label: "Athletics", color: "#7c3aed" },
    showHealth && { label: "Health", color: "#0891b2" },
    showServices && { label: "Services", color: "#ea580c" },
    showDorms && { label: "Dorms", color: "#15803d" },
    showGreek && { label: "Greek", color: "#b45309" },
  ].filter(Boolean);

  const routeColor = {};
  (campus.routes || []).forEach((r) => { routeColor[r.id] = r.color || COLORS.bus; });
  const routeName = {};
  (campus.routes || []).forEach((r) => { routeName[r.id] = r.name; });

  return (
    <div className="relative w-full h-full" data-testid="campus-map">
    <MapContainer
      center={CENTER}
      zoom={15}
      className="w-full h-full"
      zoomControl={false}
    >
      <TileLayer url={dark
        ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"} />
      <Recenter point={focus} />
      <MapReady onReady={setMapObj} />

      {showBuildings &&
        buildingList.map((b) => (
          <Marker key={b.id} position={[b.lat, b.lng]} icon={pin(COLORS[b.type] || COLORS.academic, bcode(b))}
            opacity={bOpacity(b)}>
            <Popup><b>{b.name}</b><br />{b.code}{b.college ? ` · ${b.college}` : ""}</Popup>
          </Marker>
        ))}

      {showAmenities &&
        amenityList.map((a) => (
          <Marker key={a.id} position={[a.lat, a.lng]} icon={pin(COLORS[a.type], a.type === "dining" ? "🍽" : a.type === "rec" ? "◎" : "📚", 26)}
            opacity={hlActive ? 0.25 : 1}>
            <Popup><b>{a.name}</b><br />{a.hours}</Popup>
          </Marker>
        ))}

      {showBus &&
        campus.bus_stops.map((s) => {
          const serving = campus.routes.filter((r) => (r.stops || []).includes(s.id));
          const colors = [...new Set(serving.map((r) => r.color || COLORS.bus))];
          const bg = colors.length === 0 ? COLORS.bus
            : colors.length === 1 ? colors[0]
            : `conic-gradient(${colors.map((c, i) =>
                `${c} ${Math.round((i * 100) / colors.length)}% ${Math.round(((i + 1) * 100) / colors.length)}%`).join(", ")})`;
          return (
            <Marker key={s.id} position={[s.lat, s.lng]} icon={stopPin(bg, colors.length > 1)}>
              <Popup>
                <b>{s.name}</b>
                {serving.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {serving.map((r) => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: r.color || COLORS.bus, display: "inline-block" }} />
                        {r.name}
                      </div>
                    ))}
                  </div>
                )}
              </Popup>
            </Marker>
          );
        })}

      {showBus &&
        campus.routes.map((r) => {
          const pts = r.stops
            .map((sid) => campus.bus_stops.find((s) => s.id === sid))
            .filter(Boolean)
            .map((s) => [s.lat, s.lng]);
          return <Polyline key={r.id} positions={pts} pathOptions={{ color: r.color, weight: 4, opacity: 0.55, dashArray: "1 8" }} />;
        })}

      {showBus &&
        buses.map((v) => (
          <Marker key={v.id} position={[v.lat, v.lng]}
            icon={busIcon(routeColor[v.route_id] || COLORS.bus, v.heading)}
            zIndexOffset={1000}>
            <Popup><b>{routeName[v.route_id] || "Bus"}</b><br />Live position</Popup>
          </Marker>
        ))}

      {userLoc && (
        <Marker position={userLoc} icon={pin(COLORS.user, "●", 20)}>
          <Popup>You are here</Popup>
        </Marker>
      )}

      {showPhones && phones.map((p) => (
        <Marker key={p.id} position={[p.lat, p.lng]} icon={pin("#1d4ed8", "✆", 24)}>
          <Popup><b>{p.name}</b><br />Emergency phone</Popup>
        </Marker>
      ))}

      {showAccess && access.map((a) => (
        <Marker key={a.id} position={[a.lat, a.lng]} icon={pin(accColor(a.type), accIcon(a.type), 24)}>
          <Popup><b>{a.name}</b><br />{a.note}</Popup>
        </Marker>
      ))}

      {showAthletics && athletics.map((v) => (
        <Marker key={v.id} position={[v.lat, v.lng]} icon={pin("#7c3aed", "🏟", 26)}>
          <Popup><b>{v.name}</b><br />{v.tag}</Popup>
        </Marker>
      ))}

      {showHealth && health.map((h) => (
        <Marker key={h.id} position={[h.lat, h.lng]} icon={pin("#0891b2", "✚", 24)}>
          <Popup><b>{h.name}</b><br />{h.tag}</Popup>
        </Marker>
      ))}

      {showDorms && dorms.map((d) => (
        <Marker key={d.id} position={[d.lat, d.lng]} icon={pin("#15803d", "⌂", 24)}>
          <Popup>
            <b>{d.name}</b>{d.area ? <><br />{d.area}</> : null}
            {onSetOrigin && (
              <><br /><button data-testid={`map-setstart-${d.id}`} onClick={() => onSetOrigin({ lat: d.lat, lng: d.lng, label: d.name })}
                style={{ marginTop: 6, background: brand, color: "#fff", border: "none", borderRadius: 6, padding: "4px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Set as start point
              </button></>
            )}
          </Popup>
        </Marker>
      ))}

      {showGreek && greek.map((g) => (
        <Marker key={g.id} position={[g.lat, g.lng]} icon={pin(g.colors?.bg || "#b45309", g.letters || "Ω", 26)}>
          <Popup>
            <b>{g.name}</b><br />{g.type}{g.council ? ` · ${g.council}` : ""}
            {onSetOrigin && (
              <><br /><button data-testid={`map-setstart-${g.id}`} onClick={() => onSetOrigin({ lat: g.lat, lng: g.lng, label: g.name })}
                style={{ marginTop: 6, background: brand, color: "#fff", border: "none", borderRadius: 6, padding: "4px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Set as start point
              </button></>
            )}
          </Popup>
        </Marker>
      ))}

      {walker && (
        <>
          <Marker position={[walker.last_lat, walker.last_lng]} icon={pin("#7c3aed", "●", 22)}>
            <Popup>{walker.walker} — {walker.eta_min} min away</Popup>
          </Marker>
          <Marker position={[walker.to_lat, walker.to_lng]} icon={pin(brand, "◎", 22)}>
            <Popup>{walker.to_label}</Popup>
          </Marker>
          <Polyline positions={[[walker.last_lat, walker.last_lng], [walker.to_lat, walker.to_lng]]}
            pathOptions={{ color: "#7c3aed", weight: 4, opacity: 0.7, dashArray: "4 8" }} />
        </>
      )}

      {legs.map((seg, i) => seg && (
        <Polyline key={i} positions={seg} pathOptions={{
          color: brand,
          weight: highlightIdx === i ? 6 : 4,
          opacity: !hlActive ? 0.9 : (highlightIdx === i ? 1 : 0.15),
        }} />
      ))}
    </MapContainer>
      <div className={`absolute top-2 right-2 z-[600] flex flex-col rounded-md overflow-hidden shadow-sm border backdrop-blur ${dark ? "border-white/15 bg-zinc-900/90" : "border-border bg-white/95"}`}>
        <button
          data-testid="map-zoom-in"
          aria-label="Zoom in"
          onClick={() => mapObj && mapObj.zoomIn()}
          className={`w-9 h-9 flex items-center justify-center text-lg font-semibold hover:bg-brand/10 hover:text-brand transition-colors leading-none ${dark ? "text-zinc-100" : "text-foreground"}`}
        >
          +
        </button>
        <div className={dark ? "h-px bg-white/15" : "h-px bg-border"} />
        <button
          data-testid="map-zoom-out"
          aria-label="Zoom out"
          onClick={() => mapObj && mapObj.zoomOut()}
          className={`w-9 h-9 flex items-center justify-center text-lg font-semibold hover:bg-brand/10 hover:text-brand transition-colors leading-none ${dark ? "text-zinc-100" : "text-foreground"}`}
        >
          −
        </button>
      </div>
      <div className="absolute bottom-2 left-2 z-[600] flex flex-col gap-1 items-start">
        {legendItems.length > 0 && (
          <div data-testid="map-legend" className="rounded-md bg-white/90 backdrop-blur px-2 py-1.5 shadow-sm border border-border">
            <div className="flex flex-wrap gap-x-3 gap-y-1 max-w-[260px]">
              {legendItems.map((it) => (
                <span key={it.label} className="flex items-center gap-1 text-[11px] font-medium text-foreground">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: it.color }} /> {it.label}
                </span>
              ))}
            </div>
          </div>
        )}
        {showBus && transitSrc && (
          <div
            data-testid="transit-source-badge"
            className="rounded-md bg-white/90 backdrop-blur px-2 py-1 text-[11px] font-medium text-foreground shadow-sm border border-border"
          >
            Live route data · {transitSrc}
          </div>
        )}
      </div>
    </div>
  );
}
